/**
 * `isRightSidebarOnScreen` — the SINGLE definition of "the Agentes Lexiun
 * pane's content is currently on screen".
 *
 * WHY THIS EXISTS AS ONE EXPORTED FUNCTION (I-4, chat-infobar miniplan):
 * `RightSidebar` branches on VIEWPORT FIRST and then reads EXACTLY ONE of
 * the two visibility slices —
 *
 *   compact (<1024px): renders on `rightDrawerOpen`; NEVER reads `rightSidebarExpanded`
 *   desktop:           renders on `rightSidebarExpanded`; NEVER reads `rightDrawerOpen`
 *
 * — so the two flags are NOT interchangeable and NOT combinable. The obvious
 * `expanded || drawerOpen` reading (the infobar plan's original I-4 default)
 * is wrong in both directions:
 *
 *   • compact + `expanded` stale-true + drawer CLOSED → the OR says "on screen"
 *     while the pane is not. A consumer suppressing on it goes permanently
 *     silent on that device — and the state is STICKY, because
 *     `rightSidebarExpanded` is PERSISTED to localStorage while `rightDrawerOpen`
 *     is per-session. Worse, it is self-inflicted: the §I.2 desktop auto-expand
 *     can stamp `expanded: true` from a compact viewport, so merely using the
 *     drawer on a phone can poison the flag for good.
 *   • desktop + `drawerOpen` stale-true + collapsed → same shape, opposite flag.
 *
 * Both consumers (`RightSidebar` itself and the infobar's mission ticker) MUST
 * import this rather than re-derive it, so a future change to the layout model
 * cannot leave them disagreeing about the same question. Same one-implementation
 * discipline as `computeCaseBriefDigest` and `resolveDisplayNameStrict`.
 *
 * NOT the same question as "is anything of the sidebar rendered": on a compact
 * viewport with the drawer closed, `RightSidebar` still renders a
 * `FloatingExpandButton`. That affordance is not the pane, carries no mission
 * content, and deliberately does NOT count as on-screen here.
 */
import { isCompactViewport, type ViewportSize } from "@/hooks/useViewportSize";

export function isRightSidebarOnScreen(
  viewport: ViewportSize,
  rightSidebarExpanded: boolean,
  rightDrawerOpen: boolean,
): boolean {
  return isCompactViewport(viewport) ? rightDrawerOpen : rightSidebarExpanded;
}
